import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, Heart, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useRef } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import apiClient from "../api/axios";
import { useThemeSettings } from "../contexts/ThemeContext";

interface MenuDto {
    id: string;
    name: string;
    url: string;
    orderIndex: number;
    openInNewWindow: boolean;
    subMenus: MenuDto[];
}

const languages = [
    { code: 'de', name: 'Deutsch', flag: '/media/public/images/flag-de-11111111.png' },
    { code: 'en', name: 'English', flag: '/media/public/images/flag-en-11111111.png' },
    { code: 'tr', name: 'Türkçe', flag: '/media/public/images/flag-tr-11111111.png' },
    { code: 'ru', name: 'Русский', flag: '/media/public/images/flag-ru-11111111.png' },
    { code: 'ar', name: 'العربية', flag: '/media/public/images/flag-ar-11111111.png' },
];



export const PublicHeader: React.FC = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    const { siteTokens } = useThemeSettings();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Fetch Public Menus dynamically
    const { data: menus, isLoading } = useQuery<MenuDto[]>({
        queryKey: ['publicMenus'],
        queryFn: async () => {
            const response = await apiClient.get('/public/menus');
            return response.data.data;
        }
    });

    const currentLang = localStorage.getItem('mosaik_language') || 'de';
    const selectedLang = languages.find(l => l.code === currentLang) || languages[0];
    const [isLangOpen, setIsLangOpen] = useState(false);
    const langRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (langRef.current && !langRef.current.contains(event.target as Node)) {
                setIsLangOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const changeLanguage = (lang: string) => {
        localStorage.setItem('mosaik_language', lang);
        i18n.changeLanguage(lang);
        window.location.reload();
    };


    const renderNavLinks = (isMobile = false) => {
        if (isLoading) {
            return <div className="text-sm text-foreground/50">{t('common.loading')}</div>;
        }

        const navItems = [];

        // Ensure Startseite is always present
        navItems.push(
            <NavLink
                key="home"
                to="/"
                end
                onClick={() => isMobile && setIsMobileMenuOpen(false)}
                className={({ isActive }) => isMobile
                    ? `block rounded-md px-3 py-2 text-base font-medium ${isActive ? "bg-primary/10 !text-primary" : "!text-foreground hover:bg-secondary hover:!text-foreground"}`
                    : `text-sm font-medium transition-colors hover:!text-primary pb-1 ${isActive ? "!text-primary border-b-2 border-primary" : "!text-foreground border-b-2 border-transparent"}`
                }
            >
                {t('common.home')}
            </NavLink>
        );

        if (menus) {
            menus.forEach((menu) => {
                if (menu.url === '/') return;

                const isExternal = menu.url.startsWith('http');

                if (isExternal) {
                    navItems.push(
                        <a
                            key={menu.id}
                            href={menu.url}
                            target={menu.openInNewWindow ? "_blank" : "_self"}
                            rel="noreferrer"
                            onClick={() => isMobile && setIsMobileMenuOpen(false)}
                            className={isMobile
                                ? "block rounded-md px-3 py-2 text-base font-medium !text-foreground hover:bg-secondary hover:!text-foreground"
                                : "text-sm font-medium transition-colors hover:!text-primary !text-foreground pb-1 border-b-2 border-transparent"
                            }
                        >
                            {menu.name}
                        </a>
                    );
                } else {
                    navItems.push(
                        <NavLink
                            key={menu.id}
                            to={menu.url}
                            end={menu.url === '/' || menu.url === ''}
                            onClick={() => isMobile && setIsMobileMenuOpen(false)}
                            className={({ isActive }) => {
                                // Specific fix: Don't highlight '/veranstaltungen' when in '/veranstaltungen/youth'
                                let customIsActive = isActive;
                                if (menu.url === '/veranstaltungen' && location.pathname.includes('/veranstaltungen/youth')) {
                                    customIsActive = false;
                                }
                                
                                return isMobile
                                    ? `block rounded-md px-3 py-2 text-base font-medium ${customIsActive ? "bg-primary/10 !text-primary" : "!text-foreground hover:bg-secondary hover:!text-foreground"}`
                                    : `text-sm font-medium transition-colors hover:!text-primary pb-1 ${customIsActive ? "!text-primary border-b-2 border-primary" : "!text-foreground border-b-2 border-transparent"}`;
                            }}
                        >
                            {menu.name}
                        </NavLink>
                    );
                }
            });
        }

        // Static Contact Link
        navItems.push(
            <NavLink
                key="kontakt"
                to="/kontakt"
                onClick={() => isMobile && setIsMobileMenuOpen(false)}
                className={({ isActive }) => isMobile
                    ? `block rounded-md px-3 py-2 text-base font-medium ${isActive ? "bg-primary/10 !text-primary" : "!text-foreground hover:bg-secondary hover:!text-foreground"}`
                    : `text-sm font-medium transition-colors hover:!text-primary pb-1 ${isActive ? "!text-primary border-b-2 border-primary" : "!text-foreground border-b-2 border-transparent"}`
                }
            >
                {t('common.contact', 'Kontakt')}
            </NavLink>
        );

        return <>{navItems}</>;
    };

    return (
        <header
            className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled
                ? "bg-background/95 backdrop-blur-md shadow-sm"
                : "bg-background shadow-none"
                }`}
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-4 mb-4">
                <div className="flex h-20 items-center justify-between">
                    {/* Logo / Org Name */}
                    <div className="flex shrink-0 items-center">
                        <Link to="/" className="flex items-center gap-2 text-2xl font-bold text-primary">
                            {siteTokens.LogoUrl ? (
                                <img src={siteTokens.LogoUrl} alt="Logo" className="h-[7.5rem] w-auto object-contain py-1" />
                            ) : (
                                <span>{siteTokens.SiteName || 'Mosaik e.V.'}</span>
                            )}
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex md:items-center md:space-x-8">
                        {renderNavLinks(false)}

                        <div className="flex items-center gap-6">
                            <div className="relative" ref={langRef}>
                                <button
                                    onClick={() => setIsLangOpen(!isLangOpen)}
                                    className="flex items-center gap-2 text-sm font-medium transition-colors text-foreground/80 hover:text-foreground outline-none py-2"
                                >
                                    <img src={selectedLang.flag} alt={selectedLang.name} className="w-4 h-3 object-cover rounded-sm shadow-sm" />
                                    <span>{selectedLang.name}</span>
                                    <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isLangOpen ? "rotate-180" : ""}`} />
                                </button>

                                <AnimatePresence>
                                    {isLangOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                            transition={{ duration: 0.15 }}
                                            className="absolute right-0 mt-2 w-48 overflow-hidden rounded-xl border border-border bg-background shadow-lg backdrop-blur-md"
                                        >
                                            <div className="py-1">
                                                {languages.map((lang) => (
                                                    <button
                                                        key={lang.code}
                                                        onClick={() => changeLanguage(lang.code)}
                                                        className={`flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-secondary ${
                                                            currentLang === lang.code ? "bg-primary/5 text-primary font-semibold" : "text-foreground/80"
                                                        }`}
                                                    >
                                                        <img src={lang.flag} alt={lang.name} className="w-5 h-3.5 object-cover rounded-sm" />
                                                        <span>{lang.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>


                            {/* CTA Button */}
                            <Link
                                to="/spenden"
                                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold !text-[#0f172a] shadow-sm transition-all hover:bg-accent/90 hover:-translate-y-0.5 border border-accent outline-none"
                            >
                                <Heart className="h-4 w-4 transition-transform group-hover:scale-110" />
                                {t('common.donate')}
                            </Link>
                        </div>
                    </nav>

                    {/* Mobile menu button */}
                    <div className="flex md:hidden">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="inline-flex items-center justify-center rounded-md p-2 text-foreground hover:bg-secondary hover:text-foreground focus:outline-none"
                        >
                            <span className="sr-only">Open main menu</span>
                            {isMobileMenuOpen ? (
                                <X className="block h-6 w-6" aria-hidden="true" />
                            ) : (
                                <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation */}
            {isMobileMenuOpen && (
                <div className="md:hidden border-t border-border bg-background">
                    <div className="space-y-1 px-4 pb-4 pt-2">
                        {renderNavLinks(true)}
                        <Link
                            to="/spenden"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-5 py-3 text-base font-semibold !text-[#0f172a] shadow-sm hover:bg-accent/90 border border-accent outline-none"
                        >
                            <Heart className="h-5 w-5" />
                            {t('common.donate')}
                        </Link>

                        <div className="mt-4 pt-4 border-t border-border/50 px-3">
                            <div className="grid grid-cols-2 gap-2">
                                {languages.map((lang) => (
                                    <button
                                        key={lang.code}
                                        onClick={() => changeLanguage(lang.code)}
                                        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all ${
                                            currentLang === lang.code 
                                                ? "bg-primary/10 text-primary font-bold border border-primary/20" 
                                                : "bg-secondary/50 text-foreground/80 hover:bg-secondary font-medium"
                                        }`}
                                    >
                                        <img src={lang.flag} alt={lang.name} className="w-6 h-4 object-cover rounded-sm" />
                                        <span>{lang.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </header>
    );
};
